import React from "react";
import { FaLightbulb, FaClipboardCheck, FaChartLine } from "react-icons/fa";
import { MdSpeed, MdEditSquare, MdDashboard, MdBrush, MdVerifiedUser } from "react-icons/md";

export default function App() {
  const featureCards = [
    {
      icon: <FaLightbulb className="text-coral-500 text-4xl mb-4 mx-auto" />,
      title: "Smart Creative Generation",
      text: "Create ads from briefs in minutes using intelligent templates and AI."
    },
    {
      icon: <FaClipboardCheck className="text-deepnavy text-4xl mb-4 mx-auto" />,
      title: "Controlled Approval",
      text: "Edit, tweak, and publish only what you love — no surprises."
    },
    {
      icon: <FaChartLine className="text-bravio-600 text-4xl mb-4 mx-auto" />,
      title: "Real-Time Analytics",
      text: "Track performance by creative. Fight fatigue. Win consistently."
    }
  ];

  return (
    <div className="font-nunito bg-white text-deepnavy">
      {/* Hero Section */}
      <header className="relative px-10 py-32 text-center bg-white text-deepnavy">
        <div className="relative z-10 max-w-5xl mx-auto">
          <img src="/images/bravio-logo.png" alt="Bravio Logo" className="mx-auto mb-8 w-96"/>
          <h1 className="font-montserrat text-5xl font-extrabold leading-tight mb-6">The bold way to build better ads.</h1>
          <p className="text-xl mb-10 max-w-2xl mx-auto text-deepnavy">Bravio helps you generate, test, and optimize ad creatives across channels — faster, smarter, and without the guesswork.</p>
          <div className="flex justify-center gap-6">
            <a href="#" className="bg-coral-500 text-deepnavy px-6 py-3 rounded-md font-bold shadow-md hover:bg-coral-600 transition duration-200">Get Early Access</a>
            <a href="#" className="bg-white border border-coral-500 text-coral-500 px-6 py-3 rounded-md font-bold shadow-md hover:bg-coral-50 transition duration-200">See How It Works</a>
          </div>
        </div>
      </header>

      {/* Interactive Demo Section */}
      <section className="px-6 py-24 max-w-6xl mx-auto text-center">
        <h2 className="font-montserrat text-4xl font-semibold mb-6 text-coral-500">Watch Bravio in Action</h2>
        <p className="text-lg mb-10 text-deepnavy">A smarter creative workflow — from brief to publish, powered by AI.</p>
        <div className="aspect-video shadow-2xl rounded-xl overflow-hidden">
          <iframe
            className="w-full h-full"
            src="https://www.youtube.com/embed/dQw4w9WgXcQ"
            title="Bravio Demo Video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </section>

      {/* Why Bravio Section (Enhanced) */}
      <section className="px-6 py-24 max-w-4xl mx-auto text-center">
        <h2 className="font-montserrat text-3xl font-semibold mb-10 text-deepnavy">Why Bravio?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 text-left">
          <div className="flex items-start gap-4">
            <MdSpeed className="text-coral-500 text-5xl mt-1" />
            <div>
              <h3 className="font-semibold text-lg text-deepnavy">Rapid Creative Generation</h3>
              <p className="text-deepnavy">Launch-ready creatives in minutes — tailor-made to your brief, not repurposed templates.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <MdEditSquare className="text-deepnavy text-5xl mt-1" />
            <div>
              <h3 className="font-semibold text-lg text-deepnavy">Brand-Aligned Editing</h3>
              <p className="text-deepnavy">We adapt visuals and messaging to your unique brand language, tone, trademarks, and IPs.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <MdBrush className="text-coral-500 text-5xl mt-1" />
            <div>
              <h3 className="font-semibold text-lg text-deepnavy">No Design Bottlenecks</h3>
              <p className="text-deepnavy">Our creative engine learns what works and iterates without slowing you down.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <MdVerifiedUser className="text-deepnavy text-5xl mt-1" />
            <div>
              <h3 className="font-semibold text-lg text-deepnavy">Built for Control & Compliance</h3>
              <p className="text-deepnavy">You approve what goes live. Bravio respects your regulatory and brand safety needs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Flip Cards for Features */}
      <section className="px-6 py-24 bg-white max-w-6xl mx-auto text-center">
        <h2 className="font-montserrat text-3xl font-semibold mb-12 text-deepnavy">What Bravio Does</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {featureCards.map((feature, index) => (
            <div key={index} className="relative h-64 perspective">
              <div className="relative w-full h-full transition-transform duration-700 transform-style preserve-3d hover:rotate-y-180">
                <div className="absolute w-full h-full backface-hidden bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl shadow-xl flex flex-col justify-center items-center p-6">
                  {feature.icon}
                  <h3 className="text-xl font-bold mt-2 text-deepnavy">{feature.title}</h3>
                </div>
                <div className="absolute w-full h-full backface-hidden bg-white rounded-xl shadow-xl flex justify-center items-center rotate-y-180 p-6">
                  <p className="text-deepnavy text-base">{feature.text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Founder Section */}
      <section className="px-6 py-24 max-w-5xl mx-auto text-center">
        <h2 className="font-montserrat text-3xl font-semibold mb-6 text-coral-500">Built for performance marketers, by performance marketers</h2>
        <p className="text-lg max-w-3xl mx-auto text-deepnavy">
          We’ve spent years inside ad accounts. We built Bravio to fix the one thing nobody else does — creative. Built to support teams that move fast and demand results.
        </p>
      </section>

      {/* Final CTA */}
      <footer className="bg-deepnavy text-white text-center px-10 py-20">
        <h2 className="font-montserrat text-3xl font-semibold mb-6">Ready to build ads that perform — on day one?</h2>
        <div className="flex justify-center gap-4">
          <a href="#" className="bg-white text-deepnavy px-6 py-3 rounded-md font-bold shadow hover:shadow-xl transition">Join the Beta</a>
          <a href="#" className="bg-white text-deepnavy px-6 py-3 rounded-md font-bold shadow hover:shadow-xl transition">Book a Demo</a>
        </div>
      </footer>
    </div>
  );
}
